/**
 * The inspector — what the selected block asks, and what its answers are worth.
 *
 * Visual Reference: `design/screens/editor-build.html`, the **Inspector** panel.
 * DECISION-014: "the mockup's **inspector panel is M1** and is built as drawn —
 * only the graph moves." So this is the one part of that export built to the
 * pixel, and the copy is the export's own, verbatim — including the field-key
 * warning, which is a promise about other people's systems and not a hint:
 *
 *   "Used in exports and by GoHighLevel. Changing it breaks existing mappings."
 *
 * SCORING IS SHOWN HERE AND OWNED ELSEWHERE. Every points control writes
 * through `editor/rubric.ts` into `rubric.criteria[]`, never onto the block —
 * B12's resolution, and `blockSchema` has no points field to write to even if
 * one tried. Same for Disqualify, which writes `rubric.disqualifiers[]`.
 *
 * A long-text question also says what happens to its answer — Story 20.7's
 * control, drawn by the `long-text` capture.
 *
 * Stories 6.3 and 20.7.
 */
import { useId, useState } from 'react'
import type { Block, Funnel } from '@leadgible/schema'
import {
  MODE_LABEL,
  OFFERED_MODES,
  fieldsReading,
  hasAnswerMode,
  modeRefusal,
  readByLine,
  setAnswerMode,
  type LongText,
  type OfferedMode,
} from './answer-mode'
import { DRAWN as ANSWER } from './answer-mode-copy'
import { BLOCK_LABEL, hasOptions, isContent, newOptionId } from './blocks'
import {
  DEFAULT_WEIGHT,
  forgetOption,
  isDisqualifying,
  pointsFor,
  setDisqualifying,
  setOptionPoints,
  syncCriterionLabel,
  weightFor,
} from './rubric'
import { edit, useEditor } from './store'
import { addOptionTo, duplicateOption, renameOption } from './vocabulary'

/** The export's verbatim warning. Never paraphrase it — it is a data promise. */
export const FIELD_KEY_WARNING =
  'Used in exports and by GoHighLevel. Changing it breaks existing mappings.'

/** The export's verbatim disqualify copy. */
export const DISQUALIFY_COPY =
  'Anyone choosing this is sent straight to the guide and never scored.'

export function Inspector({
  funnel,
  block,
  onOpenQualify,
}: {
  funnel: Funnel
  block: Block | null
  /** Switches the editor to its Qualify tab in place. Without it, the link navigates. */
  onOpenQualify?: () => void
}) {
  if (!block) {
    return (
      <p className="m-0 text-mdx-15 text-mdx-slate">
        Select a block to edit what it asks and what its answers are worth.
      </p>
    )
  }

  const stepIndex = funnel.steps.findIndex((s) => s.blocks.includes(block.id))
  const step = funnel.steps[stepIndex]

  return (
    <div className="flex flex-col gap-mdx-20">
      <div className="flex flex-wrap items-center justify-between gap-mdx-8">
        <span className="text-mdx-17 font-mdx-semibold tracking-mdx-h3">
          {BLOCK_LABEL[block.type]}
        </span>
        {step ? (
          <span className="text-mdx-13 text-mdx-slate">
            Step {stepIndex + 1} · {step.title ?? `Step ${stepIndex + 1}`}
          </span>
        ) : null}
      </div>

      {isContent(block.type) ? (
        <ContentFields block={block} />
      ) : (
        <>
          <QuestionFields block={block} />
          <Validation block={block} />
          {hasAnswerMode(block) ? (
            // Keyed, so a refusal said about one question is not still showing
            // when the inspector moves to the next.
            <AnswerMode key={block.id} funnel={funnel} block={block} onOpenQualify={onOpenQualify} />
          ) : null}
          {hasOptions(block.type) ? <Options funnel={funnel} block={block} /> : null}
        </>
      )}
    </div>
  )
}

/* ── the question half ──────────────────────────────────────────────────── */

function QuestionFields({ block }: { block: Block }) {
  if (!('question' in block)) {
    // `hidden_field` — a field key and nothing a respondent ever sees.
    return 'field_key' in block ? <FieldKey block={block} /> : null
  }

  const set = (label: string, apply: (target: Block, next: string) => void) => (next: string) =>
    edit(
      label,
      (draft) => {
        const target = draft.blocks.find((b) => b.id === block.id)
        if (target) apply(target, next)
      },
      { coalesce: true },
    )

  return (
    <>
      <Field label="Question">
        {(id) => (
          <Input
            id={id}
            value={block.question}
            onChange={(next) =>
              edit(
                'Edit the question',
                (draft) => {
                  const target = draft.blocks.find((b) => b.id === block.id)
                  if (target && 'question' in target) target.question = next
                  // The criterion is what the evidence rows say out loud, so it
                  // follows the question rather than keeping the old wording.
                  syncCriterionLabel(draft, block.id, next)
                },
                { coalesce: true },
              )
            }
          />
        )}
      </Field>

      <Field label="Help text" hint="Shown under the question on the form.">
        {(id) => (
          <Input
            id={id}
            value={block.help_text ?? ''}
            onChange={set('Edit the help text', (target, next) => {
              if (!('help_text' in target)) return
              // Absent, not empty: the schema makes it optional and an empty
              // string would render a blank line under the question.
              if (next.length === 0) delete (target as { help_text?: string }).help_text
              else target.help_text = next
            })}
          />
        )}
      </Field>

      <Toggle
        label="Required"
        on={block.required}
        onChange={(on) =>
          edit(on ? 'Make it required' : 'Make it optional', (draft) => {
            const target = draft.blocks.find((b) => b.id === block.id)
            if (target && 'required' in target) target.required = on
          })
        }
      />

      <FieldKey block={block} />
    </>
  )
}

function FieldKey({ block }: { block: Block }) {
  if (!('field_key' in block)) return null
  return (
    <Field label="Field key" hint={FIELD_KEY_WARNING}>
      {(id) => (
        <Input
          id={id}
          value={block.field_key}
          onChange={(next) =>
            edit(
              'Change the field key',
              (draft) => {
                const target = draft.blocks.find((b) => b.id === block.id)
                if (target && 'field_key' in target) target.field_key = next
              },
              { coalesce: true },
            )
          }
        />
      )}
    </Field>
  )
}

function ContentFields({ block }: { block: Block }) {
  const set = (label: string, apply: (target: Block, next: string) => void) => (next: string) =>
    edit(
      label,
      (draft) => {
        const target = draft.blocks.find((b) => b.id === block.id)
        if (target) apply(target, next)
      },
      { coalesce: true },
    )

  if (block.type === 'intro') {
    return (
      <Field label="Heading">
        {(id) => (
          <Input
            id={id}
            value={block.heading}
            onChange={set('Edit the heading', (target, next) => {
              if (target.type === 'intro') target.heading = next
            })}
          />
        )}
      </Field>
    )
  }

  if (block.type === 'rich_text') {
    return (
      <Field label="Text" hint="Markdown — bold, italic, links and lists.">
        {(id) => (
          <Input
            id={id}
            value={block.body}
            onChange={set('Edit the text', (target, next) => {
              if (target.type === 'rich_text') target.body = next
            })}
          />
        )}
      </Field>
    )
  }

  return (
    <p className="m-0 text-mdx-15 text-mdx-slate">
      {BLOCK_LABEL[block.type]} has nothing to configure — it renders where it sits.
    </p>
  )
}

/* ── options, and what they are worth ───────────────────────────────────── */

function Options({ funnel, block }: { funnel: Funnel; block: Block }) {
  /**
   * WHICH ROW SHOWS ITS CONTROLS.
   *
   * The export draws exactly one option expanded — a tinted box carrying the
   * Disqualify toggle, Duplicate, Delete and the explanation — and its other
   * three as a bare handle, label and score. At 320px that density is the
   * difference between an inspector that fits and one that scrolls past its
   * own Options heading.
   *
   * Expanded means: the row being worked on, or a row that disqualifies. The
   * second half is why the export's expanded row is the disqualifying one — a
   * hard gate has to announce itself whether or not anybody is looking at it.
   */
  const [active, setActive] = useState<string | null>(null)
  if (!('options' in block)) return null
  const weight = weightFor(funnel, block.id) ?? DEFAULT_WEIGHT

  // The vocabulary's `set_option` (Story 27.2). No `value` on a new option —
  // Q-044: `value ?? id` must stay equal to `id`, and the operation takes none.
  const addOption = () => {
    const id = newOptionId()
    edit('Add an option', (draft) => addOptionTo(draft, block.id, id))
  }

  return (
    <div className="flex flex-col gap-mdx-10 border-t border-mdx-line-soft pt-mdx-18">
      <div className="flex flex-wrap items-baseline justify-between gap-mdx-8">
        <span className="text-mdx-14 font-mdx-semibold">Options</span>
        <span className="text-mdx-13 text-mdx-slate">Score out of {weight}</span>
      </div>

      {block.options.map((option) => {
        const points = pointsFor(funnel, block.id, option.id)
        const disqualifies = isDisqualifying(funnel, block.id, option.id)
        const expanded = active === option.id || disqualifies
        const last = block.options.length === 1

        return (
          <div
            key={option.id}
            onFocusCapture={() => setActive(option.id)}
            onClick={() => setActive(option.id)}
            className={[
              'flex flex-col gap-mdx-8',
              expanded ? 'rounded-mdx-control bg-mdx-ground p-mdx-12' : '',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            <div className="flex items-center gap-mdx-8">
              <span
                aria-hidden="true"
                className="w-mdx-14 flex-none cursor-grab text-center text-mdx-14 text-mdx-slate"
              >
                ⠿
              </span>
              <Input
                ariaLabel="Option label"
                className="min-w-0 flex-1"
                value={option.label}
                onChange={(next) =>
                  edit('Rename an option', (draft) => renameOption(draft, block.id, option.id, next))
                }
              />
              <input
                type="text"
                inputMode="numeric"
                aria-label={`Score for ${option.label}`}
                value={points ?? ''}
                onChange={(event) => {
                  const next = Number(event.target.value.replace(/[^0-9]/g, ''))
                  edit(
                    'Score an option',
                    (draft) =>
                      setOptionPoints(draft, block, option.id, Number.isFinite(next) ? next : 0),
                    { coalesce: true },
                  )
                }}
                className="h-mdx-input w-mdx-56 flex-none rounded-mdx-control border border-mdx-line bg-mdx-white p-mdx-10 text-right text-mdx-15 font-mdx-semibold tabular-nums text-mdx-ink outline-none focus:border-mdx-brand"
              />
            </div>

            {expanded ? (
              <>
                <div className="flex flex-wrap items-center gap-x-mdx-12 gap-y-mdx-10 pl-mdx-22">
                  <Toggle
                    small
                    tone="review"
                    label="Disqualify"
                    on={disqualifies}
                    onChange={(on) =>
                      edit(
                        on ? 'Disqualify on this option' : 'Stop disqualifying on this option',
                        (draft) => setDisqualifying(draft, block, option.id, on),
                      )
                    }
                  />
                  <span className="ml-auto flex gap-mdx-6">
                    <SmallButton
                      onClick={() => {
                        const id = newOptionId()
                        edit('Duplicate an option', (draft) => duplicateOption(draft, block.id, id, option.label))
                      }}
                    >
                      Duplicate
                    </SmallButton>
                    <SmallButton
                      danger
                      // `options` is `min(1)`; deleting the last one writes a
                      // funnel the validator refuses, so the control goes away
                      // rather than producing a 422 on the next autosave.
                      disabled={last}
                      onClick={() =>
                        edit('Delete an option', (draft) => {
                          const target = draft.blocks.find((b) => b.id === block.id)
                          if (target && 'options' in target) {
                            target.options = target.options.filter((o) => o.id !== option.id)
                          }
                          forgetOption(draft, block.id, option.id)
                        })
                      }
                    >
                      Delete
                    </SmallButton>
                  </span>
                </div>

                {disqualifies ? (
                  <span className="pl-mdx-22 text-mdx-13 text-mdx-review-ink">
                    {DISQUALIFY_COPY}
                  </span>
                ) : null}
              </>
            ) : null}
          </div>
        )
      })}

      <button
        type="button"
        onClick={addOption}
        className="cursor-pointer self-start rounded-mdx-control-sm border-0 bg-mdx-brand-tint px-mdx-16 py-mdx-8 text-mdx-13 font-mdx-semibold text-mdx-brand-deep"
      >
        Add an option
      </button>
    </div>
  )
}

/* ── the export's own controls ──────────────────────────────────────────── */

/**
 * A labelled field, with the label really attached to the input.
 *
 * `children` is a FUNCTION of the generated id, and `<label htmlFor>` is what
 * the export uses too. A `<span>` that merely sits above an input looks
 * identical and names nothing: the field is unreachable by label to a screen
 * reader, and `getByLabel` finds nothing — which is how this was caught.
 *
 * The hint sits OUTSIDE the label on purpose. Inside, it would join the
 * accessible name, and the field key's name would become "Field key Used in
 * exports and by GoHighLevel. Changing it breaks existing mappings."
 */
function Field({
  label,
  hint,
  children,
}: {
  label: string
  hint?: string
  children: (id: string) => React.ReactNode
}) {
  const id = useId()
  return (
    <div className="flex flex-col gap-mdx-6">
      <label htmlFor={id} className="text-mdx-14 font-mdx-semibold">
        {label}
      </label>
      {children(id)}
      {hint ? <span className="text-mdx-13 text-mdx-slate">{hint}</span> : null}
    </div>
  )
}

function Input({
  id,
  value,
  onChange,
  ariaLabel,
  className = '',
}: {
  id?: string
  value: string
  onChange: (next: string) => void
  ariaLabel?: string
  className?: string
}) {
  return (
    <input
      type="text"
      id={id}
      aria-label={ariaLabel}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className={[
        'h-mdx-input w-full rounded-mdx-control border border-mdx-line bg-mdx-white',
        'px-mdx-14 py-mdx-10 text-mdx-15 text-mdx-ink outline-none',
        'focus:border-mdx-brand',
        className,
      ].join(' ')}
    />
  )
}

/** The export's pill switch — 40×24, or 34×20 in the option row. */
function Toggle({
  label,
  on,
  onChange,
  small = false,
  tone = 'brand',
}: {
  label: string
  on: boolean
  onChange: (on: boolean) => void
  small?: boolean
  tone?: 'brand' | 'review'
}) {
  const track = on ? (tone === 'review' ? 'bg-mdx-review' : 'bg-mdx-brand') : 'bg-mdx-line'
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      onClick={() => onChange(!on)}
      className={[
        'flex cursor-pointer items-center gap-mdx-8 border-0 bg-transparent p-0 font-mdx-semibold',
        small ? 'text-mdx-13' : 'text-mdx-15 font-mdx-regular',
        on && tone === 'review' ? 'text-mdx-review-ink' : 'text-mdx-ink',
      ].join(' ')}
    >
      <span
        aria-hidden="true"
        className={[
          'flex flex-none items-center rounded-mdx-pill transition-colors ease-mdx-swift',
          small ? 'h-mdx-20 w-mdx-icon-button p-mdx-2' : 'h-mdx-24 w-mdx-40 p-mdx-3',
          on ? 'justify-end' : 'justify-start',
          track,
        ].join(' ')}
      >
        <span
          className={[
            'rounded-mdx-pill bg-mdx-white',
            small ? 'size-mdx-16' : 'size-mdx-18',
          ].join(' ')}
        />
      </span>
      {label}
    </button>
  )
}

function SmallButton({
  children,
  onClick,
  danger = false,
  disabled = false,
}: {
  children: React.ReactNode
  onClick: () => void
  danger?: boolean
  disabled?: boolean
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={[
        'rounded-mdx-control-sm border border-mdx-line bg-mdx-white px-mdx-10 py-mdx-6',
        'text-mdx-13 font-mdx-semibold disabled:cursor-not-allowed disabled:opacity-mdx-recessed',
        danger ? 'text-mdx-error' : 'text-mdx-ink',
        disabled ? '' : 'cursor-pointer',
      ].join(' ')}
    >
      {children}
    </button>
  )
}

/* ── validation — DECISION-069 ──────────────────────────────────────────── */

/**
 * Two modes, plus a bounds control when the second is chosen.
 *
 * `design/screen-manifest.md` quoted this as three phrases — "Exactly one
 * answer" / "One or more answers" / "Between two and four answers" — which are
 * sentences, not an enum. Q-046 found what that costs: the 2 and the 4 are an
 * EXAMPLE in a mockup, so a three-item enum would write one mockup's example
 * numbers into `config` and make them product policy by accident.
 *
 * DECISION-069 answered it as `single` / `multi` plus min and max, which is what
 * `packages/schema`'s `selection`, `min_select` and `max_select` already are —
 * so this authors the schema rather than a parallel vocabulary. The bounds UI
 * had no Visual Reference; DECISION-070's pass drew it.
 *
 * `card_select` ONLY. `dropdown` has no `selection` field at all, so the control
 * does not render for it — DECISION-069's implementation note, and the schema
 * agrees.
 */
function Validation({ block }: { block: Block }) {
  if (block.type !== 'card_select') return null
  const multi = block.selection === 'multi'

  const setMode = (next: 'single' | 'multi') =>
    edit(next === 'multi' ? 'Allow several answers' : 'Allow one answer', (draft) => {
      const target = draft.blocks.find((b) => b.id === block.id)
      if (!target || target.type !== 'card_select') return
      target.selection = next
      // Leaving bounds behind on a single-select block would be invisible state
      // that `funnelSchema` then refuses — it rejects `max_select > 1` when
      // `selection` is `single`. Clearing them is what makes the toggle safe to
      // flip back and forth.
      if (next === 'single') {
        delete target.min_select
        delete target.max_select
      }
    })

  const setBound = (which: 'min_select' | 'max_select', raw: string) =>
    edit(
      'Change how many answers are allowed',
      (draft) => {
        const target = draft.blocks.find((b) => b.id === block.id)
        if (!target || target.type !== 'card_select') return
        const trimmed = raw.trim()
        const parsed = Number(trimmed)
        // Blank means unbounded at that end, which is the field being ABSENT
        // rather than zero — the export says so ("Leave either blank and that
        // end is unbounded") and the schema marks both `.optional()`.
        if (trimmed === '' || !Number.isInteger(parsed) || parsed < 0) {
          delete target[which]
          return
        }
        target[which] = parsed
      },
      { coalesce: true },
    )

  return (
    <div className="flex flex-col gap-mdx-8">
      <span className="text-mdx-14 font-mdx-semibold">Validation</span>

      <div role="group" aria-label="Validation" className="flex flex-wrap gap-mdx-8">
        <ModeButton label="One answer" on={!multi} onSelect={() => setMode('single')} />
        <ModeButton label="Several answers" on={multi} onSelect={() => setMode('multi')} />
      </div>

      {multi ? (
        <div className="flex flex-wrap items-end gap-mdx-12 rounded-mdx-control bg-mdx-ground p-mdx-12">
          {/*
            The export fixes these at 78px and 104px, which `design/tokens.css`
            has no token for — so they share the panel instead of being sized to
            two invented numbers. Listed as a deviation, and on Q-050 with the
            rest of what the pass turned up.
          */}
          <Bound
            label="at least"
            ariaLabel="Answer at least"
            value={block.min_select}
            onChange={(next) => setBound('min_select', next)}
          />
          <Bound
            label="at most"
            ariaLabel="Answer at most"
            placeholder="No limit"
            value={block.max_select}
            onChange={(next) => setBound('max_select', next)}
          />
          <span className="w-full text-mdx-13 text-mdx-slate">
            Both optional. Leave either blank and that end is unbounded.
          </span>
        </div>
      ) : null}
    </div>
  )
}

function ModeButton({
  label,
  on,
  onSelect,
  describedBy,
}: {
  label: string
  on: boolean
  onSelect: () => void
  /** The id of a line explaining why the last press did nothing. */
  describedBy?: string
}) {
  return (
    <button
      type="button"
      aria-pressed={on}
      aria-describedby={describedBy}
      onClick={onSelect}
      className={[
        'cursor-pointer rounded-mdx-control border px-mdx-16 py-mdx-10 text-mdx-15 font-mdx-semibold',
        on
          ? 'border-mdx-brand bg-mdx-brand-tint text-mdx-brand-deep'
          : 'border-mdx-line bg-mdx-white text-mdx-ink',
      ].join(' ')}
    >
      {label}
    </button>
  )
}

/** One end of the range. Numeric by `inputmode`, not by `type` — the export
 *  draws a text field, and `type="number"` brings spinners nothing else here has. */
function Bound({
  label,
  ariaLabel,
  placeholder,
  value,
  onChange,
}: {
  label: string
  ariaLabel: string
  placeholder?: string
  value: number | undefined
  onChange: (next: string) => void
}) {
  const id = useId()
  return (
    <span className="flex min-w-0 flex-1 flex-col gap-mdx-6">
      <label htmlFor={id} className="text-mdx-13 text-mdx-slate">
        {label}
      </label>
      <input
        id={id}
        type="text"
        inputMode="numeric"
        aria-label={ariaLabel}
        placeholder={placeholder}
        value={value === undefined ? '' : String(value)}
        onChange={(event) => onChange(event.target.value)}
        className="h-mdx-input w-full rounded-mdx-control border border-mdx-line bg-mdx-white px-mdx-12 py-mdx-10 text-right text-mdx-15 text-mdx-ink outline-none tabular-nums focus:border-mdx-brand"
      />
    </span>
  )
}

/* ── what happens to a long-text answer — Story 20.7 ────────────────────── */

/**
 * The drawn two-option control, built the way Validation's is, and the lines
 * under it.
 *
 * Only the chosen option's line shows. With `ai_extraction` chosen, the fields
 * that read the question are named, and the Qualify tab where they are defined
 * is one click away. A refused move keeps the control where it was and says,
 * in the input's error treatment, which fields to remove first — in place of
 * the other lines, as the drawing's refused specimen draws it.
 *
 * The rules are `editor/answer-mode.ts`'s; this only renders them.
 */
function AnswerMode({
  funnel,
  block,
  onOpenQualify,
}: {
  funnel: Funnel
  block: LongText
  onOpenQualify?: () => void
}) {
  const refusalId = useId()
  // The move the last press asked for, if it was refused. The sentence is
  // recomputed from the funnel on every render, so once the fields are gone, so
  // is the refusal — the arrangement a refused field removal has on the Qualify
  // tab.
  const [refusedMove, setRefusedMove] = useState<OfferedMode | null>(null)
  const funnelId = useEditor((s) => s.funnelId) ?? funnel.meta.id
  const mode = block.free_text_mode
  const refusal = refusedMove === null ? null : modeRefusal(funnel, block.id, refusedMove)
  const readBy = readByLine(fieldsReading(funnel, block.id))

  const choose = (next: OfferedMode) => {
    if (modeRefusal(funnel, block.id, next) !== null) {
      setRefusedMove(next)
      return
    }
    setRefusedMove(null)
    edit(next === 'ai_extraction' ? 'Pull facts out of the answer' : 'Keep the answer with the lead', (draft) =>
      setAnswerMode(draft, block.id, next),
    )
  }

  return (
    <div className="flex flex-col gap-mdx-8">
      <span className="text-mdx-14 font-mdx-semibold">{ANSWER.label}</span>

      {/* `rules` is not offered (DECISION-141). A question already in it
          presses neither, and either one moves it. */}
      <div role="group" aria-label={ANSWER.label} className="flex flex-wrap gap-mdx-8">
        {OFFERED_MODES.map((option) => (
          <ModeButton
            key={option}
            label={MODE_LABEL[option]}
            on={mode === option}
            describedBy={refusal !== null && option === refusedMove ? refusalId : undefined}
            onSelect={() => choose(option)}
          />
        ))}
      </div>

      {refusal !== null ? (
        <span id={refusalId} role="alert" className="text-mdx-13 text-mdx-error">
          {refusal}
        </span>
      ) : mode === 'capture_only' ? (
        <span className="text-mdx-13 text-mdx-slate">{ANSWER.keepLine}</span>
      ) : mode === 'ai_extraction' ? (
        <>
          <span className="text-mdx-13 text-mdx-slate">{ANSWER.pullLine}</span>
          {readBy !== null ? <span className="text-mdx-13 text-mdx-slate">{readBy}</span> : null}
          <a
            href={`/funnels/${funnelId}/qualify`}
            onClick={(event) => {
              // A modified click is the browser's — a new tab or window. A plain
              // one switches tab in place, as the tab strip does: a navigation
              // would reload the app and, with the change just made still
              // waiting to save, stop on the unsaved-changes prompt.
              if (!onOpenQualify || event.button !== 0) return
              if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
              event.preventDefault()
              onOpenQualify()
            }}
            className="self-start py-mdx-2 text-mdx-13 font-mdx-semibold text-mdx-brand-deep no-underline"
          >
            {ANSWER.openQualify}
          </a>
        </>
      ) : null}
    </div>
  )
}
